import React from 'react';
import { 
  Clock, 
  AlertCircle, 
  Search, 
  Filter, 
  Download, 
  CheckCircle
} from 'lucide-react';

export const AuditLogs: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-2 font-medium">
            <span>Cardio-Onc AI</span>
            <span>/</span>
            <span className="text-raspberry-500 uppercase">Governance</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">AI Governance Logs</h2>
          <p className="text-slate-500 text-sm md:text-base">Immutable audit trail of clinical decision support records.</p>
        </div>
        <button className="w-full md:w-auto bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-5 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2">
          <Download size={18} /> Export for Audit
        </button>
      </header>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search by case, query, or risk level..." 
            className="w-full border border-slate-300 rounded-xl pl-12 pr-4 py-3 text-slate-900 text-sm md:text-base focus:ring-2 focus:ring-raspberry-500 focus:border-raspberry-500 outline-none transition-all"
          />
        </div>
        <button className="w-full sm:w-auto bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-6 py-3 rounded-xl font-medium transition-colors shadow-sm flex items-center justify-center gap-2">
          <Filter size={20} /> Filters
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Timestamp</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Clinical Context</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Risk Grade</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Governance Status</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Review Req.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-5 whitespace-nowrap text-sm text-slate-600 font-medium">2:16:17 AM</td>
                <td className="px-6 py-5">
                  <p className="font-bold text-slate-900 mb-1">Interpret ECG: Sinus rhythm with QTc of 490ms in breast cancer patient on...</p>
                  <p className="text-sm text-slate-500 line-clamp-2">QTc is prolonged (&gt;480ms). Recommend baseline electrolyte check and review of concomitant medications. Trastuzumab is not typically associated with QTc prolongation but patient may be on...</p>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-md">Moderate Risk</span>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                    <CheckCircle size={16} /> Logged & Signed
                  </div>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-amber-600 text-sm font-bold">
                    <AlertCircle size={16} /> Yes
                  </div>
                </td>
              </tr>
              <tr className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-5 whitespace-nowrap text-sm text-slate-600 font-medium">1:16:17 AM</td>
                <td className="px-6 py-5">
                  <p className="font-bold text-slate-900 mb-1">Risk profile for patient starting Fluorouracil (5-FU).</p>
                  <p className="text-sm text-slate-500 line-clamp-2">5-FU is associated with coronary vasospasm. Patient should be screened for pre-existing CAD. Monitor for chest pain during infusion. Low risk if no history of ischemic heart disease.</p>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-md">Low Risk</span>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                    <CheckCircle size={16} /> Logged & Signed
                  </div>
                </td>
                <td className="px-6 py-5 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-emerald-600 text-sm font-bold">
                    <CheckCircle size={16} /> No
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="text-center mt-8">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center justify-center gap-2">
          <Clock size={14} /> End of Audit Trail
        </p>
      </div>
    </div>
  );
};
